import React from 'react';
import { Box } from '@mui/material';

/**
 * Self-constructing mini storyflow graph used as the editor preloader.
 * Nodes pop in sequentially, edges draw with marching dashes, sparks
 * travel along the connections. Loops indefinitely.
 */
export default function StoryflowLoader({ label = 'Loading storyflow' }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: '#09090b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 4,
        overflow: 'hidden',
        // Faint violet wash + dot grid mirroring the ReactFlow canvas
        backgroundImage: `
          radial-gradient(ellipse 50% 35% at 50% 50%, rgba(139,92,246,0.05), transparent 70%),
          radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)
        `,
        backgroundSize: 'auto, 22px 22px',
      }}
    >
      <style>{loaderCss}</style>

      <svg
        className="sfl-graph"
        viewBox="0 0 280 160"
        width="320"
        height="184"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Edge paths — declared once, referenced by visible strokes and motion */}
          <path id="sfl-e1" d="M 46 80 C 80 80, 90 38, 130 38" />
          <path id="sfl-e2" d="M 46 80 C 80 80, 90 122, 130 122" />
          <path id="sfl-e3" d="M 150 38 C 190 38, 200 80, 234 80" />
          <path id="sfl-e4" d="M 150 122 C 190 122, 200 80, 234 80" />
        </defs>

        {/* Edge underlays (faint static rails) */}
        {['sfl-e1', 'sfl-e2', 'sfl-e3', 'sfl-e4'].map((id) => (
          <use key={`u-${id}`} href={`#${id}`} className="sfl-edge-rail" />
        ))}

        {/* Animated marching-dash strokes */}
        <use href="#sfl-e1" className="sfl-edge sfl-edge-1" />
        <use href="#sfl-e2" className="sfl-edge sfl-edge-2" />
        <use href="#sfl-e3" className="sfl-edge sfl-edge-3" />
        <use href="#sfl-e4" className="sfl-edge sfl-edge-4" />

        {/* Sparks travelling along the paths */}
        {['sfl-e1', 'sfl-e2', 'sfl-e3', 'sfl-e4'].map((id, i) => (
          <circle
            key={`s-${id}`}
            r="2.2"
            fill="#ddd6fe"
            className="sfl-spark"
            style={{ animationDelay: `${0.9 + i * 0.18}s` }}
          >
            <animateMotion
              dur="1.8s"
              repeatCount="indefinite"
              begin={`${0.9 + i * 0.18}s`}
              keyPoints="0;1"
              keyTimes="0;1"
              calcMode="spline"
              keySplines="0.4 0 0.2 1"
            >
              <mpath href={`#${id}`} />
            </animateMotion>
          </circle>
        ))}

        {/* Nodes — flat solid disks */}
        <circle className="sfl-node sfl-node-1" cx="38" cy="80" r="9" fill="#8b5cf6" />
        <circle className="sfl-node sfl-node-2" cx="140" cy="38" r="9" fill="#8b5cf6" />
        <circle className="sfl-node sfl-node-3" cx="140" cy="122" r="9" fill="#8b5cf6" />
        <circle className="sfl-node sfl-node-4" cx="242" cy="80" r="9" fill="#8b5cf6" />
      </svg>

      <Box
        component="span"
        className="sfl-label"
        sx={{
          fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'rgba(226, 232, 240, 0.72)',
        }}
      >
        {label}
        <span className="sfl-dots">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </Box>
    </Box>
  );
}

const loaderCss = `
  .sfl-graph { overflow: visible; }

  .sfl-edge-rail {
    fill: none;
    stroke: rgba(139, 92, 246, 0.12);
    stroke-width: 1.4;
    stroke-linecap: round;
  }

  .sfl-edge {
    fill: none;
    stroke: #8b5cf6;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-dasharray: 6 6;
    stroke-dashoffset: 240;
    opacity: 0;
    animation:
      sfl-edge-draw 0.9s cubic-bezier(0.65, 0, 0.35, 1) forwards,
      sfl-edge-march 1.2s linear infinite 1.0s;
  }
  .sfl-edge-1 { animation-delay: 0.35s, 1.25s; }
  .sfl-edge-2 { animation-delay: 0.45s, 1.35s; }
  .sfl-edge-3 { animation-delay: 0.85s, 1.75s; }
  .sfl-edge-4 { animation-delay: 0.95s, 1.85s; }

  @keyframes sfl-edge-draw {
    0%   { stroke-dashoffset: 240; opacity: 0; }
    20%  { opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 1; }
  }
  @keyframes sfl-edge-march {
    to { stroke-dashoffset: -240; }
  }

  .sfl-node {
    opacity: 0;
    transform-origin: center;
    transform-box: fill-box;
    animation:
      sfl-node-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
      sfl-node-pulse 2.4s ease-in-out infinite;
  }
  .sfl-node-1 { animation-delay: 0.05s, 1.6s; }
  .sfl-node-2 { animation-delay: 0.7s, 1.9s; }
  .sfl-node-3 { animation-delay: 0.8s, 2.0s; }
  .sfl-node-4 { animation-delay: 1.25s, 2.2s; }

  @keyframes sfl-node-pop {
    0%   { opacity: 0; transform: scale(0.2); }
    60%  { opacity: 1; transform: scale(1.18); }
    100% { opacity: 1; transform: scale(1); }
  }
  @keyframes sfl-node-pulse {
    0%, 100% { fill: #8b5cf6; }
    50%      { fill: #a78bfa; }
  }

  .sfl-spark {
    opacity: 0;
    animation: sfl-spark-fade 1.8s ease-in-out infinite;
  }
  @keyframes sfl-spark-fade {
    0%, 100% { opacity: 0; }
    15%, 85% { opacity: 1; }
  }

  .sfl-label .sfl-dots {
    display: inline-block;
    margin-left: 4px;
    letter-spacing: 0.12em;
  }
  .sfl-label .sfl-dots span {
    display: inline-block;
    opacity: 0.25;
    animation: sfl-dot-wave 1.6s ease-in-out infinite;
  }
  .sfl-label .sfl-dots span:nth-child(1) { animation-delay: 0s; }
  .sfl-label .sfl-dots span:nth-child(2) { animation-delay: 0.18s; }
  .sfl-label .sfl-dots span:nth-child(3) { animation-delay: 0.36s; }
  @keyframes sfl-dot-wave {
    0%, 60%, 100% { opacity: 0.22; transform: translateY(0); }
    30%           { opacity: 1;    transform: translateY(-2px); }
  }

  @media (prefers-reduced-motion: reduce) {
    .sfl-edge, .sfl-node, .sfl-spark, .sfl-label .sfl-dots span {
      animation-duration: 0.001s !important;
      animation-iteration-count: 1 !important;
      opacity: 1 !important;
    }
  }
`;
